import { useEffect, useMemo, useState } from 'react';
import { Sidebar } from '../layout/Sidebar';
import { TopBar } from '../layout/TopBar';
import { useOrders } from '../../hooks/useOrders';
import { useAuth } from '../../contexts/AuthContext';
import { usersService, User } from '../../services/users.service';
import { rbacService, PermissionItem, RbacRole } from '../../services/rbac.service';
import { Shield, Plus, Save, RefreshCw, Users, Lock, Search, CheckSquare, Square } from 'lucide-react';
import { toast } from 'sonner';

interface RbacManagementPageProps {
  onNavigate?: (page: string) => void;
}

type Tab = 'roles' | 'matrix' | 'assign';

// DB slugs stay stable in PostgreSQL — only this map needs updating to rename or reorder groups in the UI.
const CATEGORY_META: Record<string, { label: string; order: number }> = {
  platform:      { label: 'Platform',             order: 1  },
  admin:         { label: 'Administration',       order: 2  },
  core:          { label: 'Core',                 order: 3  },
  orders:        { label: 'Orders',               order: 4  },
  operations:    { label: 'Operations',           order: 5  },
  catalog:       { label: 'Menu & Catalog',       order: 6  },
  reporting:     { label: 'Analytics & Reports',  order: 7  },
  crm:           { label: 'Customers',            order: 8  },
  communication: { label: 'Notifications & Messaging',        order: 9  },
  engagement:    { label: 'Reviews',              order: 10 },
};

const getCategoryLabel  = (slug: string) => CATEGORY_META[slug]?.label ?? slug.charAt(0).toUpperCase() + slug.slice(1);
const getCategoryOrder  = (slug: string) => CATEGORY_META[slug]?.order ?? 99;

export function RbacManagementPage({ onNavigate }: RbacManagementPageProps) {
  const [sidebarOpen, setSidebarOpen] = useState(() => (typeof window !== 'undefined' ? window.innerWidth >= 768 : true));
  const { orders: ordersData, stats: ordersStats } = useOrders({ page: 1, limit: 50 });
  const { user } = useAuth();

  const [activeTab, setActiveTab] = useState<Tab>('roles');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [rbacApiError, setRbacApiError] = useState<string>('');

  const [roles, setRoles] = useState<RbacRole[]>([]);
  const [permissions, setPermissions] = useState<PermissionItem[]>([]);
  const [users, setUsers] = useState<User[]>([]);

  const [selectedRoleId, setSelectedRoleId] = useState<string>('');
  const [permissionSelection, setPermissionSelection] = useState<Set<string>>(new Set());
  const [search, setSearch] = useState('');
  const [permissionSearch, setPermissionSearch] = useState('');
  const [showLegacyPermissions, setShowLegacyPermissions] = useState(false);

  const [showCreate, setShowCreate] = useState(false);
  const [createForm, setCreateForm] = useState({
    name: '',
    code: '',
    description: '',
    scope: 'TENANT' as 'PLATFORM' | 'TENANT',
  });

  const [assignmentMap, setAssignmentMap] = useState<Record<string, Set<string>>>({});

  const canManageRoles = user?.permissions?.includes('roles');

  const loadAll = async () => {
    setLoading(true);
    setRbacApiError('');

    const [rolesResult, permsResult, usersResult] = await Promise.allSettled([
      rbacService.getRoles(),
      rbacService.getPermissions(),
      usersService.getUsers({ page: 1, limit: 200 }),
    ]);

    const getErrorMessage = (reason: any, fallback: string) => {
      if (!reason) return fallback;
      if (typeof reason?.message === 'string') return reason.message;
      if (typeof reason === 'string') return reason;
      return fallback;
    };

    const is404 = (reason: any) => Number(reason?.status) === 404;

    if (rolesResult.status === 'fulfilled') {
      const nextRoles = rolesResult.value.data || [];
      setRoles(nextRoles);
      if (!selectedRoleId && nextRoles.length > 0) {
        setSelectedRoleId(nextRoles[0].id);
      }
    }

    if (permsResult.status === 'fulfilled') {
      setPermissions(permsResult.value.data || []);
    }

    if (usersResult.status === 'fulfilled') {
      try {
        let rawUsers: any = usersResult.value.data;
        
        // Handle nested data structure: { data: { data: [...], meta: {...} } }
        if (rawUsers && typeof rawUsers === 'object' && 'data' in rawUsers && Array.isArray(rawUsers.data)) {
          rawUsers = rawUsers.data;
        }
        
        // Ensure we always have an array
        if (!Array.isArray(rawUsers)) {
          rawUsers = [];
        }
        
        setUsers(rawUsers);
      } catch (err) {
        console.error('Error parsing users response:', err);
        setUsers([]);
      }
    } else if (usersResult.status === 'rejected') {
      console.error('Users request failed:', usersResult.reason);
      setUsers([]);
      toast.error(getErrorMessage(usersResult.reason, 'Failed to load users for role assignment'));
    } else {
      setUsers([]);
    }

    if (rolesResult.status === 'rejected' || permsResult.status === 'rejected') {
      const rolesErr = rolesResult.status === 'rejected' ? rolesResult.reason : null;
      const permsErr = permsResult.status === 'rejected' ? permsResult.reason : null;
      const hitMissingRbacApi = is404(rolesErr) || is404(permsErr);

      if (hitMissingRbacApi) {
        setRbacApiError('RBAC API endpoints were not found (404). Restart backend on latest code and ensure /api/rbac/* routes are available.');
      }

      const msg = getErrorMessage(rolesErr || permsErr, 'Failed to load RBAC roles/permissions');
      toast.error(msg);
    }

    setLoading(false);
  };

  useEffect(() => {
    loadAll();
  }, []);

  useEffect(() => {
    if (!selectedRoleId) {
      setPermissionSelection(new Set());
      return;
    }
    const selected = roles.find((r) => r.id === selectedRoleId);
    const selectedKeys = new Set((selected?.rolePermissions || []).map((rp) => rp.permission.key));
    setPermissionSelection(selectedKeys);
  }, [selectedRoleId, roles]);

  useEffect(() => {
    if (users.length === 0 || roles.length === 0) return;
    const defaults: Record<string, Set<string>> = {};
    for (const u of users) {
      const matched = roles.find((r) => r.code === (u.role || '').toUpperCase());
      defaults[u.id] = matched ? new Set([matched.id]) : new Set();
    }
    setAssignmentMap((prev) => {
      const merged: Record<string, Set<string>> = { ...defaults };
      for (const key of Object.keys(prev)) {
        merged[key] = prev[key];
      }
      return merged;
    });
  }, [users, roles]);

  const groupedPermissions = useMemo(() => {
    const legacyPermissionKeys = new Set([
      'analytics',
      'orders',
      'menus',
      'reviews',
      'settings',
      'users',
      'customers',
      'notifications',
      'tables',
    ]);

    const actionPriority = (key: string) => {
      if (key.endsWith('_read')) return 1;
      if (key.endsWith('_stats')) return 2;
      if (key.endsWith('_create')) return 3;
      if (key.endsWith('_manage')) return 4;
      if (key.endsWith('_approve')) return 5;
      if (key.endsWith('_moderate')) return 6;
      if (key.endsWith('_admin')) return 7;
      return 8;
    };

    const q = permissionSearch.trim().toLowerCase();

    const visible = permissions.filter((item) => {
      if (!showLegacyPermissions && legacyPermissionKeys.has(item.key)) {
        return false;
      }

      if (!q) return true;

      return (
        item.key.toLowerCase().includes(q) ||
        item.label.toLowerCase().includes(q) ||
        item.category.toLowerCase().includes(q)
      );
    });

    return visible.reduce<Record<string, PermissionItem[]>>((acc, item) => {
      if (!acc[item.category]) acc[item.category] = [];
      acc[item.category].push(item);
      acc[item.category].sort((a, b) => {
        const byAction = actionPriority(a.key) - actionPriority(b.key);
        if (byAction !== 0) return byAction;
        return a.key.localeCompare(b.key);
      });
      return acc;
    }, {});
  }, [permissions, permissionSearch, showLegacyPermissions]);

  const filteredRoles = useMemo(() => {
    if (!search.trim()) return roles;
    const q = search.toLowerCase();
    return roles.filter((r) => r.name.toLowerCase().includes(q) || r.code.toLowerCase().includes(q));
  }, [roles, search]);

  const togglePermission = (key: string) => {
    setPermissionSelection((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const savePermissionMatrix = async () => {
    if (!selectedRoleId) return;
    setSaving(true);
    try {
      await rbacService.setRolePermissions(selectedRoleId, { permissionKeys: Array.from(permissionSelection) });
      toast.success('Role permissions updated');
      await loadAll();
    } catch (err: any) {
      toast.error(err?.message || 'Failed to update permissions');
    } finally {
      setSaving(false);
    }
  };

  const createRole = async () => {
    if (!createForm.name.trim() || !createForm.code.trim()) {
      toast.error('Role name and code are required');
      return;
    }
    setSaving(true);
    try {
      await rbacService.createRole({
        name: createForm.name.trim(),
        code: createForm.code.trim(),
        description: createForm.description.trim() || undefined,
        scope: createForm.scope,
        tenantId: createForm.scope === 'TENANT' ? user?.tenantId : undefined,
      });
      toast.success('Role created successfully');
      setShowCreate(false);
      setCreateForm({ name: '', code: '', description: '', scope: 'TENANT' });
      await loadAll();
    } catch (err: any) {
      toast.error(err?.message || 'Failed to create role');
    } finally {
      setSaving(false);
    }
  };

  const toggleUserRole = (userId: string, roleId: string) => {
    setAssignmentMap((prev) => {
      const existing = prev[userId] ? new Set(prev[userId]) : new Set<string>();
      if (existing.has(roleId)) existing.delete(roleId);
      else existing.add(roleId);
      return { ...prev, [userId]: existing };
    });
  };

  const saveUserRoles = async (targetUser: User) => {
    const roleIds = Array.from(assignmentMap[targetUser.id] || new Set<string>());
    setSaving(true);
    try {
      await rbacService.assignUserRoles(targetUser.id, {
        roleIds,
        tenantId: targetUser.tenantId,
      });
      toast.success(`Updated roles for ${targetUser.firstName} ${targetUser.lastName}`);
    } catch (err: any) {
      toast.error(err?.message || 'Failed to assign roles');
    } finally {
      setSaving(false);
    }
  };

  if (!canManageRoles) {
    return (
      <div className="flex h-screen bg-gray-50 dark:bg-gray-950">
        <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} activePage="rbac" onNavigate={onNavigate} ordersCount={ordersData.length} />
        <div className={`flex-1 flex flex-col overflow-hidden transition-all duration-300 ${sidebarOpen ? 'min-[768px]:ml-64' : 'ml-0'}`}>
          <TopBar onMenuClick={() => setSidebarOpen(!sidebarOpen)} sidebarOpen={sidebarOpen} onNavigate={onNavigate} />
          <main className="flex-1 overflow-y-auto p-6">
            <div className="max-w-[900px] mx-auto bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-8 text-center">
              <Shield className="w-10 h-10 mx-auto mb-3 text-amber-500" />
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">Access Restricted</h2>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">You need the roles permission to manage RBAC.</p>
            </div>
          </main>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-gray-50 dark:bg-gray-950 overflow-hidden">
      <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} activePage="rbac" onNavigate={onNavigate} ordersCount={ordersStats?.total || ordersData.length} />

      <div className={`flex-1 flex flex-col overflow-hidden transition-all duration-300 ${sidebarOpen ? 'min-[768px]:ml-64' : 'ml-0'}`}>
        <TopBar onMenuClick={() => setSidebarOpen(!sidebarOpen)} sidebarOpen={sidebarOpen} onNavigate={onNavigate} />

        <main className="flex-1 overflow-y-auto p-6">
          <div className="max-w-[1400px] mx-auto">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-indigo-100 dark:bg-indigo-900/30 rounded-lg">
                  <Shield className="w-6 h-6 text-indigo-600 dark:text-indigo-400" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Access Control (RBAC)</h1>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Manage database roles, permissions, and user assignments</p>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <button onClick={loadAll} disabled={loading} className="px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800">
                  <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                </button>
                <button onClick={() => setShowCreate(true)} className="flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-sm font-semibold">
                  <Plus className="w-4 h-4" />
                  New Role
                </button>
              </div>
            </div>

            <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-3 mb-5 flex gap-2">
              <button onClick={() => setActiveTab('roles')} className={`px-4 py-2 rounded-lg text-sm font-semibold ${activeTab === 'roles' ? 'bg-indigo-600 text-white' : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'}`}>Role List</button>
              <button onClick={() => setActiveTab('matrix')} className={`px-4 py-2 rounded-lg text-sm font-semibold ${activeTab === 'matrix' ? 'bg-indigo-600 text-white' : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'}`}>Permission Matrix</button>
              <button onClick={() => setActiveTab('assign')} className={`px-4 py-2 rounded-lg text-sm font-semibold ${activeTab === 'assign' ? 'bg-indigo-600 text-white' : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'}`}>User Role Assignments</button>
            </div>

            {rbacApiError && (
              <div className="mb-5 rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-800 dark:border-amber-800/80 dark:bg-amber-950/40 dark:text-amber-200">
                {rbacApiError}
              </div>
            )}

            {activeTab === 'roles' && (
              <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-5">
                <div className="mb-4 relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search role by name/code" className="w-full pl-10 pr-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-sm" />
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-700">
                        <th className="py-2">Role</th>
                        <th className="py-2">Code</th>
                        <th className="py-2">Scope</th>
                        <th className="py-2">Permissions</th>
                        <th className="py-2">Assignments</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredRoles.map((role) => (
                        <tr key={role.id} className="border-b border-gray-100 dark:border-gray-800">
                          <td className="py-2 font-semibold text-gray-900 dark:text-white">{role.name}</td>
                          <td className="py-2 text-gray-600 dark:text-gray-300">{role.code}</td>
                          <td className="py-2 text-gray-600 dark:text-gray-300">{role.scope}</td>
                          <td className="py-2 text-gray-600 dark:text-gray-300">{role.rolePermissions?.length || 0}</td>
                          <td className="py-2 text-gray-600 dark:text-gray-300">{role._count?.assignments || 0}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {activeTab === 'matrix' && (
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
                <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-4 lg:col-span-1">
                  <h3 className="font-semibold text-gray-900 dark:text-white mb-3">Select Role</h3>
                  <div className="space-y-2 max-h-[520px] overflow-y-auto">
                    {roles.map((role) => (
                      <button key={role.id} onClick={() => setSelectedRoleId(role.id)} className={`w-full text-left px-3 py-2 rounded-lg border text-sm ${selectedRoleId === role.id ? 'border-indigo-500 bg-indigo-100 dark:bg-indigo-800/40 text-indigo-900 dark:text-indigo-100' : 'border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800'}`}>
                        <div className="font-semibold">{role.name}</div>
                        <div className="text-xs opacity-80">{role.code}</div>
                      </button>
                    ))}
                  </div>
                </div>

                <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-4 lg:col-span-2">
                  <div className="flex flex-col gap-3 mb-4">
                    <div className="flex items-center justify-between gap-3">
                      <h3 className="font-semibold text-gray-900 dark:text-white">Permission Matrix</h3>
                      <button onClick={savePermissionMatrix} disabled={saving || !selectedRoleId} className="flex items-center gap-2 px-3 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-sm font-semibold disabled:opacity-50">
                        <Save className="w-4 h-4" /> Save Permissions
                      </button>
                    </div>

                    <div className="flex flex-col md:flex-row md:items-center gap-3">
                      <div className="relative flex-1">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <input
                          value={permissionSearch}
                          onChange={(e) => setPermissionSearch(e.target.value)}
                          placeholder="Search permissions by key, label, or category"
                          className="w-full pl-10 pr-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-sm"
                        />
                      </div>

                      <label className="inline-flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                        <input
                          type="checkbox"
                          checked={showLegacyPermissions}
                          onChange={(e) => setShowLegacyPermissions(e.target.checked)}
                          className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                        />
                        Show legacy coarse keys
                      </label>
                    </div>
                  </div>

                  <div className="space-y-4 max-h-[520px] overflow-y-auto pr-1">
                    {Object.entries(groupedPermissions)
                      .sort(([a], [b]) => getCategoryOrder(a) - getCategoryOrder(b))
                      .map(([category, items]) => (
                        <div key={category} className="border border-gray-200 dark:border-gray-700 rounded-lg p-3">
                          <h4 className="font-semibold text-sm text-gray-900 dark:text-white mb-2">{getCategoryLabel(category)}</h4>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                            {items.map((perm) => {
                              const checked = permissionSelection.has(perm.key);
                              return (
                                <button key={perm.id} onClick={() => togglePermission(perm.key)} className={`flex items-center gap-2 px-2.5 py-2 rounded-lg text-left border text-sm ${checked ? 'border-indigo-500 bg-indigo-100 dark:bg-indigo-800/40 text-indigo-900 dark:text-indigo-100' : 'border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800'}`}>
                                  {checked ? <CheckSquare className="w-4 h-4" /> : <Square className="w-4 h-4" />}
                                  <span>
                                    {perm.label}{' '}
                                    <span className={`text-xs ${checked ? 'text-indigo-700 dark:text-indigo-200' : 'text-gray-500 dark:text-gray-400'}`}>
                                      ({perm.key})
                                    </span>
                                  </span>
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      ))}

                    {Object.keys(groupedPermissions).length === 0 && (
                      <div className="border border-dashed border-gray-300 dark:border-gray-700 rounded-lg p-6 text-center">
                        <p className="text-sm text-gray-600 dark:text-gray-400">No permissions match your current filter.</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'assign' && (
              <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-5">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-semibold text-gray-900 dark:text-white">Assign Roles to Users</h3>
                  <button onClick={loadAll} disabled={loading} className="px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800">
                    <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                  </button>
                </div>

                {loading && (
                  <div className="flex items-center justify-center py-12">
                    <RefreshCw className="w-6 h-6 text-indigo-600 dark:text-indigo-400 animate-spin" />
                    <span className="ml-3 text-gray-600 dark:text-gray-300">Loading users and roles...</span>
                  </div>
                )}

                {!loading && users.length === 0 && (
                  <div className="text-center py-12">
                    <Users className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
                    <p className="text-gray-600 dark:text-gray-400 font-medium">No users found</p>
                    <p className="text-sm text-gray-500 dark:text-gray-500 mt-1">Create users first to assign roles</p>
                  </div>
                )}

                {!loading && roles.length === 0 && users.length > 0 && (
                  <div className="text-center py-12">
                    <Lock className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
                    <p className="text-gray-600 dark:text-gray-400 font-medium">No roles found</p>
                    <p className="text-sm text-gray-500 dark:text-gray-500 mt-1">Create roles first before assigning them to users</p>
                  </div>
                )}

                {!loading && users.length > 0 && roles.length > 0 && (
                  <div className="space-y-4">
                    {users.map((u) => {
                      const selected = assignmentMap[u.id] || new Set<string>();
                      return (
                        <div key={u.id} className="border border-gray-200 dark:border-gray-700 rounded-xl p-4">
                          <div className="flex items-center justify-between gap-3 mb-3">
                            <div className="min-w-0">
                              <p className="font-semibold text-gray-900 dark:text-white truncate">{u.firstName} {u.lastName}</p>
                              <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{u.email}</p>
                            </div>
                            <button onClick={() => saveUserRoles(u)} disabled={saving} className="px-3 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-xs font-semibold disabled:opacity-50">Save</button>
                          </div>
                          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                            {roles.map((role) => {
                              const checked = selected.has(role.id);
                              return (
                                <button key={role.id} onClick={() => toggleUserRole(u.id, role.id)} className={`px-2.5 py-2 rounded-lg text-xs border text-left ${checked ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/20 text-indigo-700 dark:text-indigo-300' : 'border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800'}`}>
                                  <div className="font-semibold">{role.name}</div>
                                  <div className="opacity-75">{role.code}</div>
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>
        </main>
      </div>

      {showCreate && (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="w-full max-w-lg bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-2xl p-5">
            <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4">Create Role</h3>
            <div className="space-y-3">
              <input value={createForm.name} onChange={(e) => setCreateForm((p) => ({ ...p, name: e.target.value }))} placeholder="Role Name" className="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-sm" />
              <input value={createForm.code} onChange={(e) => setCreateForm((p) => ({ ...p, code: e.target.value }))} placeholder="Role Code (e.g. BRANCH_AUDITOR)" className="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-sm" />
              <textarea value={createForm.description} onChange={(e) => setCreateForm((p) => ({ ...p, description: e.target.value }))} placeholder="Description" rows={3} className="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-sm" />
              <select value={createForm.scope} onChange={(e) => setCreateForm((p) => ({ ...p, scope: e.target.value as 'PLATFORM' | 'TENANT' }))} className="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-sm">
                <option value="TENANT">TENANT</option>
                <option value="PLATFORM">PLATFORM</option>
              </select>
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={() => setShowCreate(false)} className="px-3 py-2 text-sm border border-gray-200 dark:border-gray-700 rounded-lg">Cancel</button>
              <button onClick={createRole} disabled={saving} className="px-3 py-2 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50">Create</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
